/**
 * `crypto.randomUUID()` is only defined in a **secure context** (https, or
 * http on localhost/127.0.0.1). When the app is served over plain http on any
 * other host — a LAN IP for an on-site demo, or the `php` Docker hostname used
 * by the e2e/CI stack — `crypto.randomUUID` is `undefined`, so calling it throws
 * a TypeError. That silently breaks every UUID-tagged request, most visibly
 * file uploads (the media upload never fires; the request crashes before it).
 *
 * `crypto.getRandomValues()` has no secure-context requirement, so we use it to
 * build an RFC-4122 v4 UUID when `randomUUID` is unavailable.
 */
export function safeRandomUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  const bytes = new Uint8Array(16)
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256)
  }

  // Set the version (4) and variant (10xx) bits per RFC 4122.
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80

  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0'))
  return (
    hex.slice(0, 4).join('') +
    '-' +
    hex.slice(4, 6).join('') +
    '-' +
    hex.slice(6, 8).join('') +
    '-' +
    hex.slice(8, 10).join('') +
    '-' +
    hex.slice(10, 16).join('')
  )
}
